import { AppIcon } from '@/components/ui/icons';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from '@/components/ui/IconButton';
import { useFocusTrap } from '../../../utils/accessibilityUtils';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  description?: string;
  initialFocusRef?: React.RefObject<HTMLElement>;
  showCloseButton?: boolean;
  closeOnEsc?: boolean;
  closeOnOverlayClick?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  description,
  size = 'md',
  initialFocusRef,
  showCloseButton = true,
  closeOnEsc = true,
  closeOnOverlayClick = true
}) => {
  // Unique IDs for accessibility attributes
  const titleId = useRef(`modal-title-${Math.floor(Math.random() * 10000)}`).current;
  const descriptionId = useRef(`modal-desc-${Math.floor(Math.random() * 10000)}`).current;

  // Focus trap to keep focus inside the modal
  const { ref: focusTrapRef } = useFocusTrap(isOpen);

  // Dynamic modal size classes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
  };

  // Handle ESC key press to close modal
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, closeOnEsc]);

  // Set focus when modal opens
  useEffect(() => {
    if (isOpen && initialFocusRef?.current) {
      initialFocusRef.current.focus();
    }
  }, [isOpen, initialFocusRef]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Handle modal not being in the DOM when closed
  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center card-padding-sm ax-overlay-scrim ax-overlay-scrim--soft"
      onClick={(e) => {
        if (closeOnOverlayClick && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={focusTrapRef}
        className={`ax-overlay-panel ax-overlay-modal w-full ${sizeClasses[size]} overflow-hidden`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={description ? descriptionId : undefined}
      >
        {title && (
          <div className="ax-overlay-header flex items-center justify-between bg-surface-card card-padding-sm">
            <h2
              id={titleId}
              className="text-lg font-semibold text-text-primary"
            >
              {title}
            </h2>
            {showCloseButton && (
              <IconButton
                label="Close dialog"
                onClick={onClose}
                tabIndex={0}
                icon={<AppIcon name="x" className="h-4 w-4" />}
              />
            )}
          </div>
        )}
        {description && (
          <div id={descriptionId} className="sr-only">
            {description}
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};

interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const ModalBody: React.FC<ModalBodyProps> = ({ children, className = '' }) => (
  <div className={`card-padding-sm ${className}`}>
    {children}
  </div>
);

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const ModalFooter: React.FC<ModalFooterProps> = ({ children, className = '' }) => (
  <div className={`ax-overlay-footer flex justify-end gap-2 card-padding-sm ${className}`}>
    {children}
  </div>
);
